// Localização: frontend/src/components/Prontuario/Profissional/ProfissionalVinculosPacientesTab.jsx
// Aba para gerenciar vínculos entre o profissional e pacientes

import React, { useState, useEffect } from 'react';
import api from '../../../api/api';
import LoadingIndicator from '../../../components/Common/LoadingIndicator';
import Alert from '../../../components/UI/Alert';
import { 
    Link2, Search, UserPlus, Users, AlertCircle, CheckCircle2, X, 
    Trash2, Plus
} from 'lucide-react';

const ProfissionalVinculosPacientesTab = ({ profissionalId, prontuarioData, onRefresh }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    // Estados para dados
    const [pacientesVinculados, setPacientesVinculados] = useState([]);
    const [todosPacientes, setTodosPacientes] = useState([]);
    
    // Estados para busca e filtros
    const [buscaPaciente, setBuscaPaciente] = useState('');
    
    // Estados para modal de vincular
    const [modalVincular, setModalVincular] = useState(false);
    const [pacientesSelecionados, setPacientesSelecionados] = useState([]);
    const [vinculando, setVinculando] = useState(false);

    // Carregar dados iniciais
    useEffect(() => {
        carregarDados();
    }, [profissionalId]);

    const carregarDados = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Carregar pacientes vinculados e todos os pacientes em paralelo
            const [vinculadosRes, todosRes] = await Promise.all([
                api.get(`/vinculos/usuario/${profissionalId}/pacientes`),
                api.get('/pacientes')
            ]);
            
            setPacientesVinculados(vinculadosRes.data?.pacientes || []);
            setTodosPacientes(todosRes.data || []);
        } catch (err) {
            console.error('Erro ao carregar dados:', err);
            setError(err.response?.data?.error || 'Erro ao carregar dados. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    // Filtrar pacientes disponíveis para vincular (excluindo já vinculados)
    const pacientesDisponiveis = todosPacientes.filter(p => {
        // Filtrar por busca
        if (buscaPaciente) {
            const busca = buscaPaciente.toLowerCase();
            if (!p.nome_completo?.toLowerCase().includes(busca)) {
                return false;
            }
        }
        
        // Excluir pacientes já vinculados
        const jaVinculado = pacientesVinculados.some(v => v.id === p.id);
        return !jaVinculado;
    });

    // Toggle seleção de paciente
    const togglePacienteSelecionado = (pacienteId) => {
        setPacientesSelecionados(prev => {
            if (prev.includes(pacienteId)) {
                return prev.filter(id => id !== pacienteId);
            }
            return [...prev, pacienteId];
        });
    };

    // Vincular pacientes
    const handleVincular = async () => {
        if (pacientesSelecionados.length === 0) {
            setError('Selecione pelo menos um paciente para vincular.');
            return;
        }

        setVinculando(true);
        setError(null);

        try {
            if (pacientesSelecionados.length === 1) {
                // Vincular um único paciente
                await api.post('/vinculos/vincular', {
                    pacienteId: pacientesSelecionados[0],
                    userId: profissionalId
                });
            } else {
                // Vincular múltiplos pacientes
                await api.post('/vinculos/vincular-multiplos', {
                    pacienteIds: pacientesSelecionados,
                    userId: profissionalId
                });
            }

            setSuccess(`Paciente(s) vinculado(s) com sucesso!`);
            setModalVincular(false);
            setPacientesSelecionados([]);
            setBuscaPaciente('');
            carregarDados();
            if (onRefresh) onRefresh();
        } catch (err) {
            console.error('Erro ao vincular:', err);
            console.error('Detalhes do erro:', {
                status: err.response?.status,
                data: err.response?.data,
                message: err.message
            });
            
            // Mensagem de erro mais específica
            let errorMessage = 'Erro ao vincular paciente(s). Tente novamente.';
            
            if (err.response?.status === 403) {
                errorMessage = err.response?.data?.error || 'Você não tem permissão para realizar esta ação.';
            } else if (err.response?.status === 404) {
                errorMessage = err.response?.data?.error || 'Paciente ou usuário não encontrado.';
            } else if (err.response?.status === 409) {
                errorMessage = err.response?.data?.error || 'Este vínculo já existe.';
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            }
            
            setError(errorMessage);
        } finally {
            setVinculando(false);
        }
    };

    // Desvincular
    const handleDesvincular = async (pacienteId) => {
        if (!window.confirm('Deseja realmente desvincular este paciente?')) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await api.post('/vinculos/desvincular', {
                pacienteId: pacienteId,
                userId: profissionalId
            });

            setSuccess('Paciente desvinculado com sucesso!');
            carregarDados();
            if (onRefresh) onRefresh();
        } catch (err) {
            console.error('Erro ao desvincular:', err);
            console.error('Detalhes do erro:', {
                status: err.response?.status,
                data: err.response?.data,
                message: err.message
            });
            
            // Mensagem de erro mais específica
            let errorMessage = 'Erro ao desvincular. Tente novamente.';
            
            if (err.response?.status === 403) {
                errorMessage = err.response?.data?.error || 'Você não tem permissão para realizar esta ação.';
            } else if (err.response?.status === 404) {
                errorMessage = err.response?.data?.error || 'Vínculo não encontrado.';
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            }
            
            setError(errorMessage);
            setLoading(false);
        }
    };

    // Filtrar pacientes vinculados por busca
    const pacientesVinculadosFiltrados = pacientesVinculados.filter(p => {
        if (!buscaPaciente) return true;
        const busca = buscaPaciente.toLowerCase();
        return p.nome_completo?.toLowerCase().includes(busca);
    });

    if (loading && pacientesVinculados.length === 0) {
        return <LoadingIndicator message="Carregando vínculos..." />;
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
            {/* Mensagens de Erro/Sucesso */}
            {error && (
                <div className="mb-4">
                    <Alert
                        type="error"
                        message={error}
                        onClose={() => setError(null)}
                    />
                </div>
            )}

            {success && (
                <div className="mb-4">
                    <Alert
                        type="success"
                        message={success}
                        onClose={() => setSuccess(null)}
                    />
                </div>
            )}

            {/* Header com botão de vincular */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <Link2 className="w-5 h-5 mr-2 text-emerald-600" />
                        Pacientes Vinculados
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        {pacientesVinculados.length} paciente(s) vinculado(s)
                    </p>
                </div>
                <button
                    onClick={() => setModalVincular(true)}
                    className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
                >
                    <UserPlus className="w-4 h-4" />
                    Vincular Pacientes
                </button>
            </div>

            {/* Busca */}
            <div className="mb-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        value={buscaPaciente}
                        onChange={(e) => setBuscaPaciente(e.target.value)}
                        placeholder="Buscar paciente..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                </div>
            </div>

            {/* Lista de Pacientes Vinculados */}
            {pacientesVinculadosFiltrados.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                    <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-gray-500 text-lg mb-2">
                        {buscaPaciente 
                            ? 'Nenhum paciente encontrado com essa busca.'
                            : 'Nenhum paciente vinculado ainda.'
                        }
                    </p>
                    {!buscaPaciente && (
                        <button
                            onClick={() => setModalVincular(true)}
                            className="mt-4 text-emerald-600 hover:text-emerald-700 font-medium"
                        >
                            Vincular primeiro paciente
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pacientesVinculadosFiltrados.map((paciente) => (
                        <div
                            key={paciente.id}
                            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                    <h4 className="font-semibold text-gray-900 mb-1">
                                        {paciente.nome_completo}
                                    </h4>
                                    {paciente.data_nascimento && (
                                        <p className="text-sm text-gray-500">
                                            Nascimento: {new Date(paciente.data_nascimento).toLocaleDateString('pt-BR')}
                                        </p>
                                    )}
                                    <p className="text-xs text-gray-400 mt-1">
                                        Vinculado em: {new Date(paciente.data_vinculo).toLocaleDateString('pt-BR')}
                                    </p>
                                </div>
                                <button
                                    onClick={() => handleDesvincular(paciente.id)}
                                    className="text-red-600 hover:text-red-800 p-1"
                                    title="Desvincular"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                            {paciente.status && (
                                <span className={`inline-block px-2 py-1 rounded text-xs ${
                                    paciente.status === 'Ativo' 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-gray-100 text-gray-800'
                                }`}>
                                    {paciente.status}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Modal de Vincular */}
            {modalVincular && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-bold text-gray-900">
                                    Vincular Pacientes
                                </h3>
                                <button
                                    onClick={() => {
                                        setModalVincular(false);
                                        setPacientesSelecionados([]);
                                        setBuscaPaciente('');
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                        </div>
                        
                        <div className="p-6">
                            <div className="mb-4">
                                <input
                                    type="text"
                                    placeholder="Buscar paciente..."
                                    value={buscaPaciente}
                                    onChange={(e) => setBuscaPaciente(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                />
                            </div>
                            
                            {pacientesDisponiveis.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    {buscaPaciente 
                                        ? 'Nenhum paciente encontrado com essa busca.'
                                        : 'Todos os pacientes já estão vinculados.'
                                    }
                                </div>
                            ) : (
                                <>
                                    <div className="space-y-2 max-h-96 overflow-y-auto mb-4">
                                        {pacientesDisponiveis.map((paciente) => (
                                            <label
                                                key={paciente.id}
                                                className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                                                    pacientesSelecionados.includes(paciente.id)
                                                        ? 'bg-emerald-50 border-emerald-500'
                                                        : 'border-gray-200 hover:border-emerald-300'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={pacientesSelecionados.includes(paciente.id)}
                                                    onChange={() => togglePacienteSelecionado(paciente.id)}
                                                    className="mr-3"
                                                />
                                                <div className="flex-1">
                                                    <div className="font-medium text-gray-900">
                                                        {paciente.nome_completo}
                                                    </div>
                                                    {paciente.data_nascimento && (
                                                        <div className="text-sm text-gray-500">
                                                            Nascimento: {new Date(paciente.data_nascimento).toLocaleDateString('pt-BR')}
                                                        </div>
                                                    )}
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                    
                                    <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                                        <button
                                            onClick={() => {
                                                setModalVincular(false);
                                                setPacientesSelecionados([]);
                                                setBuscaPaciente('');
                                            }}
                                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={handleVincular}
                                            disabled={vinculando || pacientesSelecionados.length === 0}
                                            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {vinculando ? 'Vinculando...' : `Vincular (${pacientesSelecionados.length})`}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfissionalVinculosPacientesTab;

